import {
  Sparkles, Coffee, FileText, Zap, Laptop, Armchair, ShieldCheck, Target,
  TrendingUp, Building2, Rocket, Users, Briefcase, Hotel, Stethoscope,
  GraduationCap, UtensilsCrossed, Warehouse, Store, Gift, Package,
  FileSearch, Scale, PackageSearch, Gauge, LayoutDashboard, Combine, PhoneCall,
} from "lucide-react";

export const CONTACT = {
  phoneDisplay: "+91 92126 36329",
  phoneIntl: "919212636329",
  email: "support@saarthva.com",
  hours: "Mon–Sat, 9:30 AM – 6:30 PM IST",
  base: "India · Growing Tier-1 city network",
};

export const RESPONSE_PROMISE = "Target response within 4 business hours during business hours.";

export const LINKEDIN_URL = "https://www.linkedin.com/company/saarthva-ventures/";

export const waLink = (text) =>
  `https://wa.me/${CONTACT.phoneIntl}?text=${encodeURIComponent(text)}`;

export const NAV = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Procurement & Supplies", path: "/products" },
  { label: "Managed Procurement", path: "/managed-procurement" },
  { label: "Operations", path: "/workplace-operations" },
  { label: "Contact", path: "/contact" },
];

export const SERVICES = [
  {
    id: "housekeeping-cleaning",
    title: "Housekeeping & Cleaning",
    cls: "OPEX",
    desc: "Cleaning chemicals, paper products, waste management and daily sanitation consumables.",
    icon: Sparkles,
    tags: ["Cleaning chemicals", "Paper products", "Waste management"],
  },
  {
    id: "pantry-beverages",
    title: "Pantry & Beverages",
    cls: "OPEX",
    desc: "Coffee, tea, beverages, snacks and everyday pantry supplies for teams of any size.",
    icon: Coffee,
    tags: ["Coffee & tea", "Beverages", "Snacks"],
  },
  {
    id: "stationery-office",
    title: "Stationery & Office Supplies",
    cls: "OPEX",
    desc: "Paper, print, desk essentials and administrative consumables — including branded stationery.",
    icon: FileText,
    tags: ["Paper & print", "Desk essentials", "Branded stationery"],
  },
  {
    id: "electrical-mro",
    title: "Electrical & MRO",
    cls: "MRO",
    desc: "Electrical, plumbing, HVAC accessories, tools, hardware and maintenance consumables.",
    icon: Zap,
    tags: ["Electrical & wiring", "Plumbing & HVAC", "Tools & hardware"],
  },
  {
    id: "it-small-appliances",
    title: "IT & Small Appliances",
    cls: "CAPEX",
    desc: "IT hardware, peripherals, networking and workplace appliances.",
    icon: Laptop,
    tags: ["IT hardware", "Peripherals", "Appliances"],
  },
  {
    id: "furniture-capex",
    title: "Furniture & CAPEX",
    cls: "CAPEX",
    desc: "Workstations, ergonomic seating, fixtures, equipment and new-office capital purchases.",
    icon: Armchair,
    tags: ["Workstations", "Seating", "Fixtures & equipment"],
  },
  {
    id: "safety-ppe",
    title: "Safety & PPE",
    cls: "MRO",
    desc: "Safety gear, PPE, first aid and fire protection for compliant workplaces.",
    icon: ShieldCheck,
    tags: ["PPE & safety gear", "First aid", "Fire safety"],
  },
  {
    id: "corporate-gifting-uniforms",
    title: "Corporate Gifting & Uniforms",
    cls: "ONE-TIME SETUP",
    desc: "Branded gifting, welcome kits, uniforms and custom apparel for teams and events.",
    icon: Gift,
    tags: ["Welcome kits", "Uniforms", "Branded gifting"],
  },
  {
    id: "packaging-consumables",
    title: "Packaging & Consumables",
    cls: "OPEX",
    desc: "Packaging material, disposables and day-to-day consumables for retail, F&B and dispatch operations.",
    icon: Package,
    tags: ["Packaging material", "Disposables", "Dispatch supplies"],
  },
  {
    id: "special-custom-sourcing",
    title: "Special / Custom Sourcing",
    cls: "ONE-TIME SETUP",
    desc: "Unusual, urgent or hard-to-find requirements — if it exists, we will find it; if it does not, we will get it made.",
    icon: Target,
    tags: ["Hard-to-find", "Urgent sourcing", "Custom fabrication"],
  },
];

export const SERVICE_PAGES = [
  {
    title: "Procurement as a Service",
    path: "/managed-procurement",
    icon: TrendingUp,
    desc: "Source → Compare → Negotiate → Order → Deliver → Track → Report. A procurement department, without building one from scratch.",
  },
  {
    title: "Operations as a Service",
    path: "/workplace-operations",
    icon: Building2,
    desc: "Coordinate → Monitor → Resolve → Audit → Review → Improve. An operations team, without building one from scratch.",
  },
];

export const SEGMENTS = [
  { label: "Corporate Offices", icon: Briefcase },
  { label: "Coworking & Managed Workspaces", icon: Users },
  { label: "Hospitality & Hotels", icon: Hotel },
  { label: "Clinics & Healthcare", icon: Stethoscope },
  { label: "Retail & Multi-location Businesses", icon: Store },
  { label: "Warehouses & Logistics", icon: Warehouse },
  { label: "Restaurants & Cafes", icon: UtensilsCrossed },
  { label: "Schools & Institutions", icon: GraduationCap },
  { label: "Startups & Growing Companies", icon: Rocket },
];

export const CAPABILITIES = [
  { title: "Multi-Category Sourcing", text: "One partner across recurring and specialised requirements." },
  { title: "Tier-1 City Network", text: "Sourcing and coordination across major Indian business hubs." },
  { title: "Procurement + Operations", text: "From requirement and sourcing through execution and follow-up." },
  { title: "One Point of Contact", text: "Fewer vendors to chase. Clearer accountability." },
];

export const PROBLEMS = [
  {
    icon: Users,
    title: "Too Many Vendors",
    text: "Multiple vendors, multiple contacts and multiple follow-ups become one accountable relationship.",
  },
  {
    icon: PhoneCall,
    title: "Teams Chasing Quotations",
    text: "We source, compare and coordinate so internal teams spend less time following up.",
  },
  {
    icon: Scale,
    title: "Inconsistent Pricing",
    text: "Centralised sourcing and comparative quotations create better commercial visibility across locations.",
  },
  {
    icon: PackageSearch,
    title: "Orders Getting Lost in Follow-ups",
    text: "We track requirements from quotation through delivery, acknowledgement and closure.",
  },
  {
    icon: Gauge,
    title: "No Vendor Performance Visibility",
    text: "Structured reviews, escalation tracking and vendor scorecards create clearer accountability.",
  },
  {
    icon: LayoutDashboard,
    title: "No Consolidated View",
    text: "Monthly procurement and operations MIS helps management see spend, orders, issues and open actions.",
  },
];

export const SPEND_CLASSES = [
  {
    code: "OPEX",
    name: "Operating Expenses",
    desc: "Recurring requirements that keep the workplace running.",
    items: ["Housekeeping", "Pantry", "Stationery", "Paper products", "Office consumables", "Uniforms", "Routine supplies"],
  },
  {
    code: "CAPEX",
    name: "Capital Expenses",
    desc: "Strategic purchases and new-office investments.",
    items: ["Furniture", "Office equipment", "IT hardware", "Appliances", "New-office requirements", "Fixtures", "Special projects"],
  },
  {
    code: "MRO",
    name: "Maintenance, Repair & Operations",
    desc: "Keep-it-running spend for facilities and equipment.",
    items: ["Electrical", "Plumbing", "HVAC accessories", "Tools", "Hardware", "Safety equipment", "Maintenance consumables", "Replacement components"],
  },
];

export const ENGAGEMENT_MODELS = [
  { icon: Zap, title: "On-Demand Sourcing", text: "For one-time or irregular procurement requirements." },
  { icon: TrendingUp, title: "Managed Procurement", text: "Ongoing sourcing and vendor management across recurring categories." },
  { icon: Building2, title: "Operations Retainer", text: "Ongoing coordination of selected workplace or business operations." },
  { icon: Combine, title: "Procurement + Operations", text: "One integrated relationship covering sourcing and execution." },
];

export const CROSSOVER_FLOW = [
  "Requirement",
  "Sourcing",
  "Vendor Selection",
  "Delivery",
  "Operational Execution",
  "SLA / Issue Tracking",
  "Review",
];

export const REQUIREMENT_TYPES = [
  "One-time purchase",
  "Recurring procurement",
  "Managed procurement",
  "Operations support",
  "New-site setup",
  "Custom sourcing",
];

export const FAQS = [
  {
    q: "What can Saarthva procure?",
    a: "Almost everything a business buys — across OPEX, CAPEX and MRO: housekeeping, pantry, stationery, electrical and maintenance supplies, IT and appliances, furniture, safety and PPE, corporate gifting, uniforms, packaging, and special or custom sourcing for unusual requirements.",
  },
  {
    q: "Can Saarthva work with our existing vendors?",
    a: "Yes. We are happy to coordinate with vendors you already trust, and we add vendors from our network where they bring better value, reliability or coverage.",
  },
  {
    q: "Do we need to outsource all procurement?",
    a: "No. You can start with one requirement, one category, one site or one operational problem — and expand only when we prove value.",
  },
  {
    q: "Can we start with one category?",
    a: "Absolutely. Housekeeping, pantry and stationery are common starting points, but any single category works.",
  },
  {
    q: "Can Saarthva support multiple locations?",
    a: "Yes. We provide central accountability with local sourcing and coordination across major Tier-1 business hubs, and other locations based on the requirement.",
  },
  {
    q: "Do you provide procurement reports?",
    a: "Yes. We provide a structured monthly MIS covering spend by category, location and vendor, order status, open issues, savings opportunities and vendor performance.",
  },
  {
    q: "What does Operations as a Service include?",
    a: "Workplace and facility coordination, vendor management, housekeeping, security and pantry coordination, repairs and maintenance, helpdesk and issue management, SOPs and controls, MIS and dashboards, new-site setup, multi-site operations and asset coordination.",
  },
  {
    q: "Can Saarthva help open a new office or location?",
    a: "Yes — give us the checklist. We coordinate sourcing and operational readiness across furniture, IT, pantry, housekeeping, electrical, safety, signage, vendors and recurring supplies until launch.",
  },
  {
    q: "Can Saarthva source unusual or custom items?",
    a: "That is a specialty. Send us the unusual, urgent and hard-to-find — our sourcing model is built for exactly that.",
  },
  {
    q: "Which cities can Saarthva support?",
    a: "Our primary hubs are Delhi NCR, Mumbai, Bengaluru, Hyderabad, Chennai, Pune, Kolkata and Ahmedabad, working through a growing network of local sourcing partners. Other locations are available based on the requirement.",
  },
];

export const CATALOGUE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/catalogue.pdf`;

export const CATEGORY_IMAGES = {
  "housekeeping-cleaning": "https://images.pexels.com/photos/33827307/pexels-photo-33827307.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "pantry-beverages": "https://images.unsplash.com/photo-1779905705972-357048611e57?crop=entropy&cs=srgb&fm=jpg&q=85",
  "stationery-office": "https://images.pexels.com/photos/5942699/pexels-photo-5942699.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "electrical-mro": "https://images.unsplash.com/photo-1781559818983-c32838ee3d55?crop=entropy&cs=srgb&fm=jpg&q=85",
  "it-small-appliances": "https://images.unsplash.com/photo-1765366417033-5d74f04ca77a?crop=entropy&cs=srgb&fm=jpg&q=85",
  "furniture-capex": "https://images.unsplash.com/photo-1765366417031-60bc8543189c?crop=entropy&cs=srgb&fm=jpg&q=85",
  "safety-ppe": "https://images.pexels.com/photos/38195854/pexels-photo-38195854.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "corporate-gifting-uniforms": "https://images.unsplash.com/photo-1770626884824-291d1f8fad45?crop=entropy&cs=srgb&fm=jpg&q=85",
  "packaging-consumables": "https://images.unsplash.com/photo-1781559818983-c32838ee3d55?crop=entropy&cs=srgb&fm=jpg&q=85",
  "special-custom-sourcing": "https://images.pexels.com/photos/33827307/pexels-photo-33827307.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

export const CITIES = [
  { name: "Delhi NCR", x: 150, y: 118, major: true },
  { name: "Ahmedabad", x: 82, y: 224, major: true },
  { name: "Mumbai", x: 92, y: 288, major: true },
  { name: "Pune", x: 114, y: 310, major: true },
  { name: "Hyderabad", x: 158, y: 300, major: true },
  { name: "Kolkata", x: 282, y: 196, major: true },
  { name: "Bengaluru", x: 150, y: 376, major: true },
  { name: "Chennai", x: 198, y: 366, major: true },
];
